import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type BaseSyntheticEvent,
  type PropsWithChildren,
} from "react";
import { nextButtonName } from "./FormWizardButtons";
import { formVariantsEnum } from "./FormWizard";
import PageFormSummaryStep from "./PageFormRenderer/PageFormSummaryStep/PageFormSummaryStep";
import type {
  FormVariantType,
  FormWizardContextValue,
  FormWizardData,
  FormWizardOrigin,
  FormWizardState,
  FormWizardStep,
} from "./types";

const initialState: FormWizardState = {
  formData: {},
  formSteps: [],
  activeStepIndex: 0,
  isFirstStep: false,
  isLastStep: false,
  isFormSubmitting: false,
  dangerSubmit: false,
  formOrigin: { link: "", name: "" },
  wizardName: "",
  onSubmit: () => {},
  wizardData: undefined,
  handleCancel: (href?: string) => {},
  submitLabel: "Submit",
  analyticsStepId: "",
};

export const FormWizardContext = createContext<FormWizardContextValue>(
  initialState as unknown as FormWizardContextValue
);

export const FormWizardProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [formState, updateFormState] = useReducer(
    (state: FormWizardState, newState: Partial<FormWizardState>) => ({
      ...state,
      ...newState,
    }),
    initialState
  );

  const {
    formData,
    formSteps,
    activeStepIndex,
    onSubmit,
    submitLabel,
    onCancel,
    handleCancel,
    isFormSubmitting,
    dangerSubmit,
    formOrigin,
    wizardName,
  } = formState;

  const activeStep = useMemo(
    () => formSteps?.[activeStepIndex],
    [activeStepIndex, formSteps]
  );

  const analyticsStepId = useMemo(
    () => `${activeStepIndex + 1}_${activeStep?.id}`,
    [activeStepIndex, activeStep]
  );

  const isLastStep = useMemo(
    () => activeStepIndex === formSteps?.length - 1,
    [activeStepIndex, formSteps]
  );

  const isFirstStep = useMemo(() => activeStepIndex === 0, [activeStepIndex]);

  const handleNextStep = useCallback(
    (updatedData: FormWizardData, valuesChanged: boolean) => {
      if (isLastStep) {
        onSubmit(updatedData);
      } else {
        updateFormState({
          activeStepIndex: activeStepIndex + 1,
          formData: updatedData,
          formSteps: formSteps.map((step, index) => {
            if (index === activeStepIndex) {
              return {
                ...step,
                completed: true,
              };
            } else if (index > activeStepIndex && !!valuesChanged) {
              return {
                ...step,
                completed: false,
              };
            }

            return step;
          }),
        });
      }
    },
    [isLastStep, activeStepIndex, onSubmit, formSteps]
  );

  const handleBackStep = useCallback(
    (updatedData: FormWizardData) => {
      updateFormState({
        activeStepIndex: activeStepIndex - 1,
        formData: updatedData,
      });
    },
    [activeStepIndex]
  );

  const handleStepSubmit = useCallback(
    (stepValues: FormWizardData, e?: BaseSyntheticEvent, isValid = true) => {
      const newFormData: FormWizardData = { ...formData, ...stepValues };

      // `submitter` is typed as HTMLElement; the submitting control is a
      // button/input that carries a `name`. Narrow to read it (behavior-identical).
      const submitter = (e?.nativeEvent as SubmitEvent | undefined)
        ?.submitter as (HTMLElement & { name?: string }) | null | undefined;
      const isNextStep = submitter?.name === nextButtonName;

      if (isNextStep) {
        if (isValid) {
          let valuesChanged = false;

          if (activeStep?.depsMap) {
            for (const [key, deps] of Object.entries(activeStep?.depsMap)) {
              if (newFormData[key] !== formData[key]) {
                valuesChanged = true;

                for (const dep of deps) {
                  newFormData[dep] = null;
                }
              }
            }
          }

          handleNextStep(newFormData, valuesChanged);
        }
      } else {
        handleBackStep(newFormData);
      }
    },
    [formData, formSteps, activeStep?.depsMap]
  );

  const initFormWizard = useCallback(
    (
      steps: FormWizardStep[],
      onCancel: (href?: string) => void,
      dangerSubmit: boolean,
      formOrigin: FormWizardOrigin,
      wizardName: string,
      wizardData: FormWizardData | undefined,
      isValidated: boolean,
      withSummary: boolean,
      type: FormVariantType,
      submitLabel?: string
    ) => {
      updateFormState({
        formSteps: [
          ...steps,
          ...(withSummary && type === formVariantsEnum.PAGE
            ? [
                {
                  id: "summary",
                  name: "Summary",
                  form: <PageFormSummaryStep steps={steps} />,
                },
              ]
            : []),
        ].map((step) => ({
          ...step,
          completed: !!isValidated && !!wizardData,
          failed: false,
        })),
        onCancel,
        dangerSubmit,
        ...(!!submitLabel && {
          submitLabel,
        }),
        formOrigin,
        wizardName,
        ...(!!wizardData && {
          formData: wizardData,
        }),
      });
    },
    []
  );

  const setSubmitting = useCallback((isSubmitting: boolean) => {
    updateFormState({
      isFormSubmitting: isSubmitting,
    });
  }, []);

  const setOnSubmit = useCallback(
    (onSubmit: (data: FormWizardData) => void) => {
      updateFormState({ onSubmit });
    },
    []
  );

  const formWizardContextValue = useMemo<FormWizardContextValue>(
    () => ({
      formData,
      formSteps,
      activeStepIndex,
      activeStep,
      isLastStep,
      isFirstStep,
      dangerSubmit,
      handleStepSubmit,
      formOrigin,
      wizardName,
      submitLabel,
      // `onCancel` is undefined until `initFormWizard` runs; fall back to the
      // reducer's no-op default so consumers can always call `handleCancel`.
      handleCancel: onCancel ?? handleCancel,
      isFormSubmitting,
      setSubmitting,
      initFormWizard,
      setOnSubmit,
      analyticsStepId,
    }),
    [
      formData,
      formSteps,
      activeStepIndex,
      activeStep,
      isLastStep,
      isFirstStep,
      isFormSubmitting,
      dangerSubmit,
      formOrigin,
      wizardName,
      submitLabel,
      handleCancel,
      onCancel,
      setOnSubmit,
      analyticsStepId,
    ]
  );

  return (
    <FormWizardContext.Provider value={formWizardContextValue}>
      {children}
    </FormWizardContext.Provider>
  );
};

const useFormWizard = (): FormWizardContextValue =>
  useContext(FormWizardContext);
export default useFormWizard;
